/**
 * "Quando" scritto come lo direbbe qualcuno che sta in Italia.
 *
 * Era in due copie — una in Messaggi, una nello Storico — e tutte e due
 * decidevano «è oggi?» con `toDateString()`, che risponde nel fuso del
 * dispositivo. Per un host che guarda Otto da Londra o con il portatile
 * ancora sull'orario di New York, un messaggio arrivato alle 00:30 italiane
 * risultava di ieri: compariva «14/09» invece di «00:30», cioè la cosa più
 * recente sembrava la più vecchia.
 *
 * Otto ragiona sempre in `Europe/Rome` — è la convenzione che il resto del
 * codice usa già per `todayInRome` — e anche questa riga deve farlo.
 */

const FUSO = "Europe/Rome";

/** Il giorno a Roma, come `2026-09-14`: confrontabile con `===`. */
export function giornoARoma(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: FUSO,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Due istanti cadono nello stesso giorno italiano? */
export function stessoGiornoARoma(a: Date, b: Date): boolean {
  return giornoARoma(a) === giornoARoma(b);
}

/**
 * L'ora se è oggi, la data se è un altro giorno.
 *
 * `formato` decide come si scrive la data: `"numerico"` dà `24/08`, che è
 * quello che vuole un elenco fitto di messaggi; `"breve"` dà `24 ago`, che
 * si legge meglio in una riga sola.
 */
export function formatWhen(
  iso: string,
  formato: "numerico" | "breve" = "numerico",
  adesso: Date = new Date(),
): string {
  const date = new Date(iso);
  const oggi = stessoGiornoARoma(date, adesso);

  return new Intl.DateTimeFormat("it-IT", {
    ...(oggi
      ? { hour: "2-digit", minute: "2-digit" }
      : formato === "numerico"
        ? // "24 ago" si legge come "24 giorni fa": meglio 24/08.
          { day: "2-digit", month: "2-digit" }
        : { day: "numeric", month: "short" }),
    timeZone: FUSO,
  }).format(date);
}

/**
 * Da quanto è ferma una lettura del gestionale, detta come la direbbe un host.
 *
 * Serve perché un dato giusto su letture vecchie è indistinguibile da uno
 * sbagliato, e finché non lo si scrive da nessuna parte nessuno se ne accorge:
 * il calendario di ieri e quello di adesso si leggono identici. Il conto lo
 * diceva già — `ContoCard` — ma solo il conto, e solo oltre le sei ore.
 *
 * Qui si dice sempre, anche quando la risposta è «adesso»: è quella la
 * rassicurazione. Il silenzio non distingue «fresco» da «non lo so».
 */
export function daQuandoLetto(iso: string | null): string | null {
  if (!iso) return null;
  const quando = Date.parse(iso);
  if (Number.isNaN(quando)) return null;

  const minuti = Math.floor((Date.now() - quando) / 60_000);
  if (minuti < 0) return "adesso";
  if (minuti < 2) return "adesso";
  if (minuti < 60) return `${minuti} minuti fa`;

  const ore = Math.floor(minuti / 60);
  if (ore < 24) return ore === 1 ? "un'ora fa" : `${ore} ore fa`;

  const giorni = Math.floor(ore / 24);
  return giorni === 1 ? "ieri" : `${giorni} giorni fa`;
}
